/**
 * Customer Scan Action Handlers
 *
 * Handles pause/resume scanning operations for both single and bulk operations.
 * Provides consistent behavior across table and drawer contexts.
 */

import { useQueryClient } from '@tanstack/react-query';

import { useAxios } from '@/hooks/useAxios';
import { CustomerData } from '@/sections/customerManagement/types';
import { useAuth } from '@/state/auth';

/**
 * Hook for customer scan pause/resume operations
 *
 * Supports both single and bulk customer scan state updates with
 * proper error handling and cache invalidation.
 *
 * @returns Object containing handlePauseScanning and handleResumeScanning functions
 *
 * @example
 * ```tsx
 * const { handlePauseScanning, handleResumeScanning } = useCustomerScanHandlers();
 *
 * // Single customer pause
 * await handlePauseScanning([customer]);
 *
 * // Bulk customer resume
 * await handleResumeScanning(selectedCustomers);
 * ```
 */
export const useCustomerScanHandlers = () => {
  const axios = useAxios();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  /**
   * Updates frozen setting for a single customer
   *
   * Uses direct API call with Account header for customer impersonation.
   * This pattern avoids calling hooks inside loops which violates React rules.
   *
   * @param email - Customer email for impersonation
   * @param frozen - Whether to freeze (true) or unfreeze (false) scanning
   * @returns Promise that resolves when update completes
   */
  const updateFrozenSetting = async (email: string, frozen: boolean) => {
    const token = await getToken();

    return axios({
      method: 'put',
      url: '/setting',
      data: {
        name: 'frozen',
        value: frozen ? 'true' : 'false',
      },
      headers: {
        Authorization: `Bearer ${token}`,
        Account: email, // Customer impersonation
      },
    });
  };

  /**
   * Pauses scanning for one or more customers
   *
   * Executes updates in parallel using Promise.allSettled to handle
   * partial failures gracefully. Invalidates relevant queries after
   * successful updates.
   *
   * @param customers - Array of customers to pause scanning (single or multiple)
   * @throws Error if all operations fail
   * @returns Object with succeeded and failed counts
   */
  const handlePauseScanning = async (customers: CustomerData[]) => {
    // Execute all updates in parallel
    const promises = customers.map(customer =>
      updateFrozenSetting(customer.email, true)
    );

    // Use Promise.allSettled to handle partial failures
    const results = await Promise.allSettled(promises);

    // Count successes and failures
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    // Invalidate relevant queries if any operations succeeded
    if (succeeded > 0) {
      queryClient.invalidateQueries({ queryKey: ['my', 'setting'] });
      queryClient.invalidateQueries({
        queryKey: ['my', { resource: 'account' }],
      });
    }

    // Log failures for debugging
    if (failed > 0) {
      const failedResults = results
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r, i) => ({
          customer: customers[i]?.email,
          error: r.reason,
        }));

      console.error(
        '[useCustomerScanHandlers] Failed to pause scanning:',
        failedResults
      );
    }

    // Throw error if all operations failed
    if (failed === customers.length) {
      throw new Error('Failed to pause scanning for all customers');
    }

    // Return success/failure counts for caller to handle messaging
    return { succeeded, failed };
  };

  /**
   * Resumes scanning for one or more customers
   *
   * Executes updates in parallel using Promise.allSettled to handle
   * partial failures gracefully. Invalidates relevant queries after
   * successful updates.
   *
   * @param customers - Array of customers to resume scanning (single or multiple)
   * @throws Error if all operations fail
   * @returns Object with succeeded and failed counts
   */
  const handleResumeScanning = async (customers: CustomerData[]) => {
    // Execute all updates in parallel
    const promises = customers.map(customer =>
      updateFrozenSetting(customer.email, false)
    );

    // Use Promise.allSettled to handle partial failures
    const results = await Promise.allSettled(promises);

    // Count successes and failures
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    // Invalidate relevant queries if any operations succeeded
    if (succeeded > 0) {
      queryClient.invalidateQueries({ queryKey: ['my', 'setting'] });
      queryClient.invalidateQueries({
        queryKey: ['my', { resource: 'account' }],
      });
    }

    // Log failures for debugging
    if (failed > 0) {
      const failedResults = results
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r, i) => ({
          customer: customers[i]?.email,
          error: r.reason,
        }));

      console.error(
        '[useCustomerScanHandlers] Failed to resume scanning:',
        failedResults
      );
    }

    // Throw error if all operations failed
    if (failed === customers.length) {
      throw new Error('Failed to resume scanning for all customers');
    }

    // Return success/failure counts for caller to handle messaging
    return { succeeded, failed };
  };

  return {
    handlePauseScanning,
    handleResumeScanning,
  };
};
